import QrScanner from 'qr-scanner';
import { getDeviceSetting, deviceSettingEvent } from './device_settings';

// One camera for the whole app session.
//
// The reason this module exists: the camera permission is owned by the browser/OS, not the page —
// there is no API to "save" or pre-grant it. What re-triggers the prompt (most painfully in an
// installed iOS PWA, where every getUserMedia call can re-prompt) is *acquiring the stream again*.
// The previous implementation stopped and re-acquired the camera on essentially every navigation
// (layer open/close, fragment swap), so the clerk was asked on every scan screen.
//
// Instead we keep a single QrScanner + <video> alive for the whole session and only MOVE its host
// node between the visible #barcode-reader and an offscreen "parking" node. getUserMedia then runs
// once, so the prompt appears once. Two lifecycles for the camera while no scan screen is showing,
// chosen by the per-device "reduce_camera_prompts" setting:
//
//   default (releaseIdle): pause *decoding* (DOM video.pause keeps the MediaStream alive) and, after
//     IDLE_RELEASE_MS with no scan screen, fully release the camera (stop the stream → the OS
//     "camera in use" indicator turns off). Quick hops (scan → quantity dialog → back) stay warm and
//     resume with no prompt; genuinely leaving scanning releases the camera and costs one prompt next
//     time. This is the default.
//   keepAlive: never release while the app is open — only pause decoding. One getUserMedia for the
//     whole session = the prompt appears once. Trade-off: the OS camera indicator stays on while the
//     app is open. Recommended for iPhone/iPad, where re-prompting is worst.
//
// Library facts this relies on (qr-scanner 1.4.x, verified against the source):
//   - start() only calls getUserMedia when video.srcObject is null; otherwise it just replays the
//     existing stream (no prompt).
//   - qrScanner.pause()/stop() ALWAYS release the stream (~300ms later), so they are unusable for
//     "keep the camera but stop scanning". Pausing the DOM <video> directly stops the decode loop
//     (_scanFrame bails while video.paused) WITHOUT releasing the stream; video.play() fires the
//     library's 'play' handler which restarts the decode loop. That pair is how we keep the stream
//     while idling.

// How long the camera stays warm (stream alive, decoding paused) after the last scan screen closes
// before it is fully released, in the default mode. Covers a slow quantity entry between scans.
const IDLE_RELEASE_MS = 30000;
const SETTING_KEY = 'reduce_camera_prompts';

let scanner = null;       // the single QrScanner for the whole session (created lazily)
let host = null;          // wrapper holding <video> + qr-scanner's overlay + flash button
let video = null;
let parking = null;       // offscreen home for `host` while no scan screen is visible
let currentReader = null; // the #barcode-reader element currently showing the camera
let attached = false;
let cold = true;          // true when the stream is released (next start() = a getUserMedia prompt)
let flashAdded = false;
let idleTimer = null;
let onScan = () => {};

function keepAlive() {
  return getDeviceSetting(SETTING_KEY, false) === true;
}

function clearIdleTimer() {
  if (idleTimer) { clearTimeout(idleTimer); idleTimer = null; }
}

function frontReader() {
  const layer = window.up && up.layer.front;
  const root = layer && layer.element;
  return root ? root.querySelector('#barcode-reader') : null;
}

function ensureScanner() {
  if (scanner) { return; }

  parking = document.createElement('div');
  parking.id = 'camera-parking';
  document.body.appendChild(parking);

  host = document.createElement('div');
  host.className = 'camera-host';

  video = document.createElement('video');
  video.className = 'scanner-video';
  host.appendChild(video);
  // Park the host (with the video already in the DOM) so qr-scanner won't relocate the <video>.
  parking.appendChild(host);

  scanner = new QrScanner(
    video,
    result => onDecode(result.data),
    {
      highlightScanRegion: true,
      highlightCodeOutline: true,
      returnDetailedScanResult: true,
      preferredCamera: 'environment',
      maxScansPerSecond: 10,
      onDecodeError: () => {}, // a frame without a code is normal; keep scanning
    },
  );
}

function onDecode(data) {
  // Frames decoded while parked or mid-navigation must be ignored; a single scan must drive a single
  // navigation. We re-establish the camera on whatever screen the scan lands on (see syncToFrontLayer).
  if (!attached) { return; }
  attached = false;
  onScan(data);
  setTimeout(syncToFrontLayer, 300);
}

async function addFlash() {
  if (flashAdded || !scanner) { return; }
  let hasFlash = false;
  try { hasFlash = await scanner.hasFlash(); } catch (error) { return; }
  if (!hasFlash) { return; }
  flashAdded = true;

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'flash-button btn rounded-circle';
  button.innerHTML = '<i class="fa-solid fa-bolt"></i>';
  button.style.position = 'absolute';
  button.style.top = '10px';
  button.style.right = '10px';
  button.style.zIndex = '1000';
  host.appendChild(button);

  // Toggle via the library's own API (see the original implementation): turning the torch off
  // restarts the stream internally, which applyConstraints({torch:false}) can't do reliably.
  button.addEventListener('click', async () => {
    if (!scanner) { return; }
    try {
      await scanner.toggleFlash();
      button.classList.toggle('active', scanner.isFlashOn());
    } catch (error) {
      console.warn('Flash toggle failed', error);
    }
  });
}

function removeSpinner(reader) {
  const spinner = reader && reader.querySelector('.loading-spinner');
  if (spinner) { spinner.remove(); }
}

async function attach(reader) {
  if (!reader || (attached && currentReader === reader)) { return; }

  ensureScanner();
  clearIdleTimer();

  reader.appendChild(host);
  currentReader = reader;
  attached = true;

  try {
    if (cold) {
      await scanner.start(); // requests the camera — the (one) permission prompt happens here
      cold = false;
      addFlash();
    } else {
      await video.play();    // warm: resume the decode loop, no getUserMedia, no prompt
    }
    if (currentReader === reader) { removeSpinner(reader); }
    // The overlay (scan-region highlight) is repositioned on window resize; force it after the move.
    window.dispatchEvent(new Event('resize'));
  } catch (error) {
    // Permission denied / camera busy: leave the spinner + reload button so the clerk can retry.
    console.warn('Camera start failed', error);
  }
}

function applyDetachedState() {
  if (!scanner) { return; }
  if (!video.paused) { video.pause(); } // stop decoding, keep the stream (camera) alive
  clearIdleTimer();
  if (!keepAlive()) {
    idleTimer = setTimeout(releaseCamera, IDLE_RELEASE_MS);
  }
}

function releaseCamera() {
  idleTimer = null;
  if (attached || !scanner) { return; }
  scanner.stop(); // releases the stream (camera indicator off); next start() will prompt again
  cold = true;
}

export const camera = {
  // The compiler injects the navigation behaviour (kept there with its preprocessors).
  configure({ onScan: handler }) {
    if (handler) { onScan = handler; }
  },

  // Single source of truth: the camera belongs to the front layer's #barcode-reader, or nowhere.
  syncToFrontLayer() {
    const reader = frontReader();
    if (reader) { attach(reader); } else { this.detach(); }
  },

  detach() {
    if (!host) { return; }
    if (parking) { parking.appendChild(host); }
    attached = false;
    currentReader = null;
    applyDetachedState();
  },

  // Called from a #barcode-reader compiler's destructor: only react if it was the live one.
  handleReaderRemoved(reader) {
    if (reader === currentReader) { this.detach(); }
  },

  // The reload button: force a fresh stream (e.g. after a permission error or a wedged camera).
  async restart() {
    ensureScanner();
    await scanner.pause(true); // release immediately
    cold = true;
    const reader = currentReader || frontReader();
    attached = false;
    currentReader = null;
    if (reader) { attach(reader); }
  },
};

// React live to the device-setting toggle (no reload): re-evaluate the parked-state policy.
document.addEventListener(deviceSettingEvent(SETTING_KEY), () => {
  if (!attached) { applyDetachedState(); }
});

// qr-scanner auto-releases the stream when the app is backgrounded and re-acquires it on return.
// After returning while no scan screen is showing, re-apply our parked policy (pause decoding and,
// in the default mode, re-arm the idle release). Deferred so it runs after qr-scanner has re-acquired.
document.addEventListener('visibilitychange', () => {
  if (!document.hidden && !attached) {
    setTimeout(() => { if (!attached) { applyDetachedState(); } }, 600);
  }
});
